import React, { createContext, useState, useEffect, useCallback } from 'react';
import { api, API_ENDPOINTS, getAuthToken, setAuthToken, removeAuthToken } from '../config/api';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../utils/constants';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Verifica si hay un token válido al cargar la aplicación
   */
  useEffect(() => {
    const token = getAuthToken();
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      } catch (err) {
        console.error('Error al parsear datos del usuario:', err);
        removeAuthToken();
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  /**
   * Inicia sesión
   * @param {string} username - Nombre de usuario
   * @param {string} password - Contraseña
   */
  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(API_ENDPOINTS.auth.login, {
        username,
        password,
      });

      if (response.access) {
        setAuthToken(response.access);
        
        // Guardar también el refresh token si viene
        if (response.refresh) {
          localStorage.setItem('refreshToken', response.refresh);
        }

        // Decodificar el JWT para obtener el user_id
        let userId = null;
        try {
          const tokenParts = response.access.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            userId = payload.user_id || payload.sub;
          }
        } catch (decodeErr) {
          console.warn('No se pudo decodificar el token:', decodeErr);
        }

        // Crear objeto de usuario con los datos disponibles
        const userData = {
          token: response.access,
          username,
          id: userId,
          rol: 'usuario', // Valor por defecto
          farmacia_id: 1, // Valor por defecto
          farmacia_nombre: 'Farmacia Principal', // Valor por defecto
          email: `${username}@farmacia.com`, // Valor por defecto
        };

        setUser(userData);
        
        // Guardar datos del usuario en localStorage para persistencia
        localStorage.setItem('user', JSON.stringify(userData));

        return { success: true, message: SUCCESS_MESSAGES.login };
      } else {
        throw new Error('No se recibió el token de autenticación');
      }
    } catch (err) {
      const errorMessage = err.message || ERROR_MESSAGES.invalidCredentials;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cierra sesión
   */
  const logout = useCallback(() => {
    removeAuthToken();
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
  }, []);

  /**
   * Refresca el token de autenticación
   */
  const refreshToken = useCallback(async () => {
    const refresh = localStorage.getItem('refreshToken');
    
    if (!refresh) {
      logout();
      return false;
    }

    try {
      const response = await api.post(API_ENDPOINTS.auth.refresh, {
        refresh,
      });

      if (response.access) {
        setAuthToken(response.access);
        
        // Actualizar el refresh token si viene uno nuevo
        if (response.refresh) {
          localStorage.setItem('refreshToken', response.refresh);
        }

        return true;
      }
      
      return false;
    } catch (err) {
      logout();
      return false;
    }
  }, [logout]);

  /**
   * Verifica si el usuario está autenticado
   */
  const isAuthenticated = useCallback(() => {
    return !!user && !!getAuthToken();
  }, [user]);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    refreshToken,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

// Made with Bob
